import { useEffect, useState } from 'react';
import { intakeApi, userApi } from '../../api/services';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';

export default function AssignToUserModal({ isOpen, onClose, intakeBatchId, onAssigned, existingAssigneeIds = [] }) {
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    userApi.assignable()
      .then((res) => {
        const rows = res.data.users || [];
        setUsers(rows);
        // Pre-tick existing assignees so the admin can see who's already on
        // the batch and add/remove from there.
        setSelectedIds(new Set(existingAssigneeIds || []));
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load users'))
      .finally(() => setLoading(false));
  }, [isOpen, existingAssigneeIds]);

  function toggle(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleAssign() {
    if (selectedIds.size === 0) {
      toast.error('Pick at least one user');
      return;
    }
    setSubmitting(true);
    try {
      const res = await intakeApi.assignToUser(intakeBatchId, { userIds: [...selectedIds] });
      toast.success(`Assigned ${res.data.ordersAssigned} order(s) to ${selectedIds.size} user(s)`);
      onAssigned?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Assignment failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign batch to Dispatch Users">
      {loading && <div className="text-sm text-gray-500">Loading users…</div>}
      {!loading && users.length === 0 && (
        <div className="text-sm text-gray-600">
          No users with the <code>dispatch_user</code> role exist yet. Create one under Admin → Users.
        </div>
      )}
      {!loading && users.length > 0 && (
        <>
          <p className="text-xs text-gray-500 mb-2">
            Pick one or more users. Multiple assignees can work on the batch in parallel —
            any of them can Submit for Review on any order. Once one user submits, the
            order moves to admin approval and the button disappears for the others.
          </p>
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg mb-3">
            {users.map((u) => (
              <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                <input
                  type="checkbox"
                  checked={selectedIds.has(u.id)}
                  onChange={() => toggle(u.id)}
                />
                <span className="text-sm">{u.first_name} {u.last_name}</span>
                <span className="text-xs text-gray-500 ml-auto">{u.email}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Only orders in <strong>intake</strong> stage will be (re)assigned. Orders that
            already moved to data-incomplete / reship-unlinked / blocked stages stay where
            they are; existing assignments on those orders are preserved.
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handleAssign} disabled={submitting || selectedIds.size === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Assigning…' : `Assign (${selectedIds.size})`}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
